// R1c — Project Command Center. Tabbed workspace under AdminAppShell.
// Reads existing `projects` collection + related contracts/documents/audit rows.
// No schema changes. Missing fields render as "—" (truthful; no invention).
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import AppShell from "../../components/AppShell";
import EmptyState from "../../components/EmptyState";
import StatusChip from "../../components/StatusChip";
import { api, formatApiError } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Boxes, FileText, Calendar, Users, DollarSign, Activity as ActivityIcon, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import ProjectTeamPanel from "../../components/projects/ProjectTeamPanel";
import { useAuth } from "../../lib/auth";
import { TENANT_WIDE } from "../../lib/projectAccess";

const TABS = [
  { key: "overview", label: "Overview", icon: Boxes },
  { key: "schedule", label: "Schedule", icon: Calendar },
  { key: "team", label: "Team", icon: Users },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "financials", label: "Financials", icon: DollarSign },
  { key: "activity", label: "Activity", icon: ActivityIcon },
];

function money(v) {
  return v == null ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function Value({ value }) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-erf-dark/50">—</span>;
  }
  return <span className="text-erf-dark">{value}</span>;
}

export default function ProjectShell() {
  const { id } = useParams();
  const { user } = useAuth();
  const backTo = TENANT_WIDE.includes(user?.role_key) ? "/admin/operations/projects" : "/my/projects";
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const activeTab = search.get("tab") || "overview";

  const [project, setProject] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    setErr("");
    api.get(`/projects/${id}`)
      .then((r) => setProject(r.data))
      .catch((e) => setErr(formatApiError(e.response?.data?.detail)));
    // Related lists — tenant-scoped by server, then filtered to this project
    api.get("/contracts").then((r) => setContracts(
      (r.data || []).filter((c) => c.project_id === id || c.opportunity_id === (project?.opportunity_id))
    )).catch(() => {});
    api.get("/documents").then((r) => setDocuments(
      (r.data || []).filter((d) => d.linked_project_id === id || d.linked_contract_id)
    )).catch(() => {});
    api.get("/audit?limit=200").then((r) => {
      const rows = (r.data && r.data.items) || [];
      setActivity(rows.filter((row) => row.resource_id === id || (row.resource_type === "project" && row.resource_id === id)));
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const setTab = (key) => {
    const next = new URLSearchParams(search);
    next.set("tab", key);
    setSearch(next, { replace: true });
  };

  if (err) {
    return (
      <AppShell title="Project" subtitle="Command Center">
        <div className="text-red-700 text-sm" data-testid="project-error">{err}</div>
      </AppShell>
    );
  }
  if (!project) {
    return (
      <AppShell title="Project" subtitle="Command Center">
        <div className="font-mono text-xs text-erf-dark/60" data-testid="project-loading">Loading…</div>
      </AppShell>
    );
  }

  const p = project;
  const relatedContracts = contracts.filter((c) => c.opportunity_id === p.opportunity_id);
  const projectDocs = documents.filter((d) => d.linked_project_id === id);

  return (
    <AppShell
      title={p.name || "Project"}
      subtitle={`Project ${String(p.id).slice(0, 8)} · created ${(p.created_at || "").slice(0, 10) || "—"}`}
      actions={
        <Link to={backTo}>
          <Button variant="outline" className="border-erf-borderCream" data-testid="project-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Projects
          </Button>
        </Link>
      }
    >
      <div
        className="flex flex-wrap items-center gap-1 bg-white border border-erf-borderCream rounded-md p-1 mb-4"
        role="tablist"
        aria-label="Project workspace tabs"
        data-testid="project-tabs"
      >
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = activeTab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              role="tab"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-nav transition-colors ${
                isActive
                  ? "bg-erf-gold text-erf-dark font-semibold"
                  : "text-erf-dark/70 hover:bg-erf-creamSecondary"
              }`}
              data-testid={`project-tab-${t.key}`}
            >
              <Icon className="w-3.5 h-3.5" aria-hidden="true" /> {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === "overview" && (
        <div data-testid="project-overview">
          <div className="flex items-center gap-2 mb-4">
            <StatusChip status={p.status === "shell" ? "not_configured" : "connected"} label={p.status || "shell"} />
            <StatusChip status="phase_n" phase={3} label="Full workspace in Phase 3" />
          </div>
          <Card className="border-erf-borderCream mb-4">
            <CardContent className="p-5 grid gap-4 md:grid-cols-2">
              <div>
                <div className="text-[11px] font-mono uppercase text-erf-dark/60">Scope summary</div>
                <div className="mt-1 font-nav text-sm whitespace-pre-wrap">
                  <Value value={p.scope_summary} />
                </div>
              </div>
              <div>
                <div className="text-[11px] font-mono uppercase text-erf-dark/60">Opportunity</div>
                <div className="mt-1 font-mono text-xs">
                  <Value value={p.opportunity_id ? String(p.opportunity_id).slice(0, 12) : null} />
                </div>
              </div>
              <div>
                <div className="text-[11px] font-mono uppercase text-erf-dark/60">Contract</div>
                <div className="mt-1 font-mono text-xs">
                  <Value value={p.contract_id ? String(p.contract_id).slice(0, 12) : null} />
                </div>
              </div>
              <div>
                <div className="text-[11px] font-mono uppercase text-erf-dark/60">Priority / % complete</div>
                <div className="mt-1 font-nav text-sm">
                  <Value value={p.priority && p.completion_pct != null ? `${p.priority} · ${p.completion_pct}%` : null} />
                </div>
              </div>
              <div>
                <div className="text-[11px] font-mono uppercase text-erf-dark/60">Budget · actual</div>
                <div className="mt-1 font-mono text-sm">
                  {p.budget != null ? money(p.budget) : "—"} · {p.actual != null ? money(p.actual) : "—"}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-mono uppercase text-erf-dark/60">Next milestone</div>
                <div className="mt-1 font-nav text-sm"><Value value={p.next_milestone} /></div>
              </div>
            </CardContent>
          </Card>
          <div className="text-[11px] font-mono text-erf-dark/60">
            Missing fields render as <span className="mono">—</span>. Suggest a field via{" "}
            <Link to="/admin/feature-requests" className="text-erf-dark underline decoration-erf-gold/60 underline-offset-2 hover:text-erf-goldHover">Feature Requests</Link>.
          </div>
        </div>
      )}

      {activeTab === "schedule" && (
        <div data-testid="project-schedule">
          <EmptyState
            icon={Calendar}
            title="Schedule editor lands in Phase 4"
            subtitle="Milestones, critical path, dependencies, and Gantt view are part of the Field Ops phase. Read-only preview will appear here once at least one milestone is captured."
          />
        </div>
      )}

      {activeTab === "team" && (
        <ProjectTeamPanel projectId={id} />
      )}

      {activeTab === "documents" && (
        <div data-testid="project-documents">
          {projectDocs.length === 0 ? (
            <EmptyState
              icon={FileText}
              title="No project documents yet"
              subtitle="Upload documents via the Document Vault and link them to this project."
            />
          ) : (
            <div className="bg-white border border-erf-borderCream rounded-md overflow-x-auto">
              <table className="w-full erf-table">
                <thead className="bg-erf-creamSecondary">
                  <tr>
                    <th className="text-left px-3 py-2">Filename</th>
                    <th className="text-left px-3 py-2">Class</th>
                    <th className="text-left px-3 py-2">Size</th>
                    <th className="text-left px-3 py-2">Uploaded</th>
                  </tr>
                </thead>
                <tbody>
                  {projectDocs.map((d) => (
                    <tr key={d.id} className="border-t border-erf-borderCream/60" data-testid={`project-doc-${d.id}`}>
                      <td className="px-3 py-2 font-nav">{d.filename}</td>
                      <td className="px-3 py-2 font-mono text-[11px]">{d.classification}</td>
                      <td className="px-3 py-2 font-mono text-[11px]">{Math.round((d.size || 0) / 1024)} kB</td>
                      <td className="px-3 py-2 font-mono text-[11px]">{(d.created_at || "").slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === "financials" && (
        <div data-testid="project-financials">
          {relatedContracts.length === 0 ? (
            <EmptyState
              icon={DollarSign}
              title="No contracts linked to this project"
              subtitle="Contracts appear here once linked via CRM → Contract → Deposit. Financial detail lives in /admin/financial/contracts."
            />
          ) : (
            <div className="space-y-2">
              {relatedContracts.map((c) => (
                <Link
                  key={c.id}
                  to={`/crm/contracts`}
                  className="block bg-white border border-erf-borderCream rounded-md p-3 hover:border-erf-gold/60"
                  data-testid={`project-contract-${c.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-nav font-semibold text-erf-dark">{c.title || `Contract ${String(c.id).slice(0, 8)}`}</div>
                      <div className="font-mono text-[11px] text-erf-dark/60 mt-0.5">{c.status || "draft"}</div>
                    </div>
                    <div className="font-mono text-sm text-erf-dark">{money(c.amount)}</div>
                  </div>
                </Link>
              ))}
              <div className="text-[11px] font-mono text-erf-dark/60 pt-2">
                Change-orders + invoices tied to this project surface in Phase 5 AP/AR.
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "activity" && (
        <div data-testid="project-activity">
          {activity.length === 0 ? (
            <EmptyState
              icon={ActivityIcon}
              title="No audit ledger entries for this project"
              subtitle="Every write against this project is captured in the immutable ledger — none have been recorded yet."
            />
          ) : (
            <div className="bg-white border border-erf-borderCream rounded-md overflow-hidden">
              {activity.map((row) => (
                <div
                  key={row.hash_chain_index}
                  className="flex items-center gap-3 px-3 py-2 border-b border-erf-borderCream/60 last:border-b-0"
                  data-testid={`project-activity-${row.hash_chain_index}`}
                >
                  <span className="font-mono text-[10px] text-erf-dark/60 w-14">#{row.hash_chain_index}</span>
                  <span className="font-mono text-[11px] text-erf-dark/70 w-32">{(row.ts || "").slice(0, 19).replace("T", " ")}</span>
                  <span className="font-nav text-sm">
                    <span className="font-semibold">{row.action}</span> · <span className="font-mono text-xs">{row.actor_role}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </AppShell>
  );
}
